// The picture for an entry, as a thing you can put on screen.
//
// `preview.ts` decides *which* picture an entry gets; this decides how to show it and how to go and
// find the entry's own photo when it has one. Both the gallery grid and the journal feed need the
// identical behaviour, and the two drifting apart would be a bug nobody notices until one surface
// leaks memory and the other does not.

import { useEffect, useState, type RefObject } from 'react';
import { fetchEntryPhotos, loadEntryPhoto, type EntryPhoto, type PhotoCrop } from '../api/entry-photos';
import { cropStyle } from './photo-crop';
import { useSession } from '../auth/SessionProvider';
import type { EntryPreview } from './preview';

/**
 * Photo lists, cached for the tab.
 *
 * Only the metadata — ids, positions, sizes — never the decrypted bytes, which belong to whichever
 * tile is currently on screen and are revoked the moment it leaves. Without this, scrolling back up
 * a long list re-asks the server which photos it already told us about. Keyed by entry id, and entry
 * ids are per-user, so a second sign-in in the same tab cannot read another account's answers.
 */
const photoLists = new Map<string, EntryPhoto[]>();

async function listPhotos(dek: CryptoKey, entryId: string, signal: AbortSignal): Promise<EntryPhoto[]> {
  const cached = photoLists.get(entryId);
  if (cached) return cached;
  const photos = await fetchEntryPhotos(dek, entryId, signal);
  photoLists.set(entryId, photos);
  return photos;
}

/** Exposed so a test can start from a known state; nothing in the app should need this. */
export function clearPhotoListCache(): void {
  photoLists.clear();
}

/**
 * The entry's own first photo, but only while the element is near the viewport.
 *
 * There is no "which entries have photos" endpoint, so finding out costs a request per entry and
 * showing one costs a download and a decrypt on top. Doing that for a few hundred entries on mount
 * would be hundreds of requests for pictures nobody has scrolled to. So a tile paints its default
 * art immediately and only asks about a photo once it is within a screen's reach.
 *
 * Leaving again is the other half: the request is aborted and the object URL revoked, because a
 * decrypted 1600px JPEG that is never revoked stays in memory for the life of the tab, and a long
 * journal is precisely where you would accumulate a few hundred of them.
 */
export function useNearbyPhoto(
  entryId: string,
  target: RefObject<Element | null>,
): { url: string; crop: PhotoCrop | null } | null {
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;

  const [near, setNear] = useState(false);
  const [photo, setPhoto] = useState<{ url: string; crop: PhotoCrop | null } | null>(null);

  useEffect(() => {
    const element = target.current;
    // jsdom and older browsers have no observer. Without one there is no honest way to know what is
    // on screen, and guessing "everything" is the failure mode this whole hook exists to avoid — so
    // the element simply keeps its default art.
    if (!element || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => setNear(entries.some((e) => e.isIntersecting)),
      // A screen's worth of lead time, so a photo is usually decrypted by the time it is scrolled to
      // and an element that wobbles across the edge does not thrash.
      { rootMargin: '300px 0px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [target]);

  useEffect(() => {
    if (!near || !dek) return;

    const controller = new AbortController();
    let objectUrl: string | null = null;

    void (async () => {
      try {
        const photos = await listPhotos(dek, entryId, controller.signal);
        if (photos.length === 0 || controller.signal.aborted) return;

        const first = [...photos].sort((a, b) => a.position - b.position)[0];
        const loaded = await loadEntryPhoto(dek, entryId, first.id, controller.signal);

        // Aborting after the bytes are already decrypted still leaves a live object URL behind.
        if (controller.signal.aborted) {
          URL.revokeObjectURL(loaded);
          return;
        }
        objectUrl = loaded;
        setPhoto({ url: loaded, crop: first.crop });
      } catch {
        // A photo that will not load is not worth an error state on a thumbnail; the default art is
        // a perfectly good picture and the entry is still one tap away.
      }
    })();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPhoto(null);
    };
  }, [near, dek, entryId]);

  return photo;
}

/**
 * A preview rendered as a `<span>`, so it is legal inside the `<button>` that both a gallery tile
 * and a feed row are. The caller supplies the class, since the two want different geometry.
 */
export function EntryArt({ preview, className }: { preview: EntryPreview; className: string }) {
  if (preview.kind === 'gradient') {
    return <span className={className} style={{ background: preview.background }} />;
  }

  return (
    <span className={className}>
      <img
        src={preview.url}
        // The writer's own framing follows their photo everywhere it appears, so a face they
        // centred in the composer is still centred in the feed and the gallery.
        style={cropStyle(preview.crop)}
        // An empty label means the picture depicts nothing in particular — an aura, or a photo the
        // caller chose not to describe — and announcing it would only pad the button's name.
        alt={preview.label}
        aria-hidden={preview.label ? undefined : true}
        loading="lazy"
        decoding="async"
        draggable={false}
      />
    </span>
  );
}